import _ from 'lodash'

import type { ProjectionParams } from 'modules/reports/store/models/Projection'

import { formatterForGraph } from '../Constants'
import { pathWithFunction } from '../projectionHelper'

export interface AxisNames {
  xAxis: string
  yAxis: string
  groupBy: string | undefined
}

export interface Builder {
  data: Array<Record<string, any>>
  projection: ProjectionParams | undefined
  aggregation: ProjectionParams | undefined
}

export interface Sql {
  data: Array<Record<string, any>>
  sqlProjections: Record<string, any>
}

/**
 * Fields - Use this in order to check if a query field is a report field or a sql field
 * @param fields - Provide a sql report or a builder report
 */
function isBuilder(fields: unknown): fields is Builder {
  return (fields as Builder).aggregation !== undefined
}

export type FieldType = {
  primaryField: string
  secondaryField: string
  tertiaryField: string | undefined
}

export class Fields {
  primaryField: string
  secondaryField: string
  tertiaryField: string | undefined

  /**
   * Fields - Use this class to store the report projections
   * @constructor
   * @param fields - Provide a sql report or a builder report
   */
  constructor(fields: FieldType) {
    const { primaryField, secondaryField, tertiaryField } = fields

    this.primaryField = primaryField
    this.secondaryField = secondaryField
    this.tertiaryField = tertiaryField
  }
}

export class Query {
  fields: Fields
  id: string
  type: 'sql' | 'builder'
  projection: ProjectionParams | undefined
  aggregation: ProjectionParams | undefined

  /**
   * Query - Use this class in order to get the projection fields of a SQL report or a builder report
   * @constructor
   * @param fields - Provide a sql report o a builder report
   */
  constructor(fields: Builder | Sql) {
    if (isBuilder(fields)) {
      this.fields = new Fields(this.builderData(fields))
      this.id = fields.projection?.function || ''
      this.type = 'builder'
      this.projection = fields.projection
      this.aggregation = fields.aggregation
    } else {
      const sqlData = this.sqlData(fields)
      this.fields = new Fields(sqlData)
      this.id = sqlData.primaryField
      this.type = 'sql'
    }
  }

  /**
   * builderData - Use this method to get the projections of a builder report.
   * @method
   * @param field - Provide a builder query in order to retrieve the projections.
   */
  private builderData(field: Builder): FieldType {
    const { data, aggregation } = field

    const aggregationProjectionAlias = aggregation ? pathWithFunction(aggregation) : ''
    const dimensions = _.difference(Object.keys(_.first(data)!), [aggregationProjectionAlias])
    const [dimensionOne, dimensionTwo] = dimensions

    return {
      primaryField: aggregationProjectionAlias,
      secondaryField: dimensionOne!,
      tertiaryField: dimensionTwo,
    }
  }

  /**
   * sqlData - Use this method to get the projections of a sql report.
   * @method
   * @param fields - Provide a sql query in order to retrieve the projections.
   */
  private sqlData(fields: Sql): FieldType {
    const { initial_data, secondary_data, tertiary_data } = fields.sqlProjections

    return {
      primaryField: initial_data,
      secondaryField: secondary_data,
      tertiaryField: tertiary_data,
    }
  }
}

export default abstract class Muncher {
  protected query: Query
  protected data: Array<Record<string, any>>

  /**
   * Muncher - You can inherit from this class in order to create your own muncher class.
   * Given that each chart needs a common interface but on the onther hand needs a different
   * implemetation to return a data structure that fits with its chart.
   * @constructor
   * @param query - Provide a builder or sql query in order to retrieve the projections
   * of the builder or the SQL report.
   */
  constructor(query: Builder | Sql) {
    this.query = new Query(query)
    this.data = query.data
  }

  /**
   * formatter - This function returns a formatter if the reports was made with builder, on the other hand
   * it will return a a function that returns the same value.
   * @method
   * @param query - Provide a builder or sql quiery in order to retrieve the projections
   * of the builder or the SQL report
   */
  formatter(projection: ProjectionParams | undefined) {
    if (this.query.type === 'builder' && projection) {
      return formatterForGraph(projection)
    }

    return (value: any) => {
      return value
    }
  }

  abstract call(): any
}
